import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useVerifyOtp, useSendEmailOtp } from '../api/staff/staffQueries.js';
import AuthLayout from '../components/auth/AuthLayout';
import OTPInput from '../components/auth/OTPInput';
import { LoadingSpinner } from '../components/ui/loading-spinner.tsx';
import useAuth from '../hooks/useAuth';

const OTP = () => {
  const [otpValue, setOtpValue] = useState('');
  const [countdown, setCountdown] = useState(60);
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email;
  const { user } = useAuth();

  const { mutate: verifyOTP, isPending: verifyOTPLoading } = useVerifyOtp();

  const { mutate: sendEmailOTP, isPending: sendEmailOTPLoading } =
    useSendEmailOtp();

  useEffect(() => {
    // Redirect if user is already verified
    if (user?.verified || user?.emailVerified) {
      toast.info('Your email is already verified');
      navigate('/');
      return;
    }

    // Redirect if no email in state
    if (!email) {
      toast.error('No email provided');
      navigate('/signup');
      return;
    }

    const initiateOTP = () => {
      sendEmailOTP(undefined, {
        onSuccess: () => {
          setCountdown(60);
          toast.success('Verification code sent successfully');
        },
        onError: () => {
          toast.error('Failed to send verification code');
          navigate('/signup');
        },
      });
    };
    initiateOTP();
  }, [sendEmailOTP, navigate, email, user]);

  // Countdown timer
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // Auto-verify when OTP is complete
  const handleInputComplete = (value) => {
    setOtpValue(value);

    if (value.length === 6) {
      verifyOTP(value, {
        onSuccess: () => {
          toast.success('Email verified successfully!');
          navigate('/');
        },
        onError: (error) => {
          toast.error(error.message || 'Invalid verification code');
        },
      });
    }
  };

  const handleResendOTP = () => {
    if (countdown > 0) return;

    sendEmailOTP(undefined, {
      onSuccess: () => {
        setCountdown(60);
        toast.success('New verification code sent to your email');
      },
    });
  };

  return (
    <AuthLayout currentStep={2}>
      <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10 w-[700px] text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Verify Your Email
        </h2>
        <p className="text-gray-500 mb-8">
          Enter the verification code sent to {email}
        </p>

        <div className="space-y-6">
          <OTPInput
            onComplete={handleInputComplete}
            value={otpValue}
            onChange={setOtpValue}
            isLoading={verifyOTPLoading}
          />

          <div className="flex items-center justify-center gap-4">
            <button
              type="button"
              onClick={handleResendOTP}
              disabled={sendEmailOTPLoading || countdown > 0}
              className="text-sm font-medium text-purple-600 hover:text-purple-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {sendEmailOTPLoading ? (
                <>
                  <LoadingSpinner size="sm" className="mr-2" />
                  Sending...
                </>
              ) : countdown > 0 ? (
                `Resend code in ${countdown}s`
              ) : (
                'Resend Code'
              )}
            </button>
          </div>
        </div>
      </div>
    </AuthLayout>
  );
};

export default OTP;
